import React from 'react'
import { Navbar, Nav } from 'react-bootstrap'
import styled from 'styled-components'
import {
	Subscription,
	LiveDrive,
	FoundersLogo,
	Hamburger,
	LogoHoriz,
	LogoVert,
	MapIcon,
	SoftwareTracker,
	McpIcon,
} from '../../utility/icons'
import { useIsActive } from '../../utility/hooks'
import { isTesla } from '../../utility/helpers'
import styles from '../../utility/styles'

// [claude] MAP-SPECIFIC Header (intentionally diverges from the main site's
// app/javascript/components/shared/Header.js). The charger map was composed
// around a left rail, so >=1200px this renders a fixed 180px rail (width =
// styles.spacing.navBar) with the Ink visual language: hairline seam, muted ->
// white links, active pill, 16px currentColor icons. Below 1200px it falls back
// to the main site's fixed 64px top bar (hamburger + collapse under 992px).
// Keep the LINK SET in sync with the main Header when it changes.

const activePaths = {
	map: ['charger_map', '^/map'],
	founders: ['founders'],
	dash: ['dash'],
	subscribe: ['subscribe', 'subscription', 'gift'],
	software: ['software-tracker'],
	mcp: ['mcp'],
}

const BREAK = '991.98px'
const RAIL_MIN = styles.sizes.desktop // 1200px — rail above, top bar below

const StyledNav = styled(Navbar)`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	height: 64px;
	padding: 0;
	background-color: ${({ theme }) => theme.base};
	border-bottom: 1px solid ${({ theme }) => theme.border};
	@media screen and (max-width: ${BREAK}) {
		height: auto;
		min-height: 64px;
	}
	@media screen and (min-width: ${RAIL_MIN}) {
		right: auto;
		bottom: 0;
		width: ${styles.spacing.navBar};
		height: 100vh;
		border-bottom: none;
		border-right: 1px solid ${({ theme }) => theme.border};
		align-items: stretch;
	}
`

const NavInner = styled.div`
	width: 100%;
	margin: 0 auto;
	padding: 0 24px;
	min-height: 64px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	@media screen and (min-width: ${RAIL_MIN}) {
		flex-direction: column;
		align-items: stretch;
		justify-content: flex-start;
		height: 100%;
		padding: 20px 8px;
	}
`

const LinkRow = styled(Nav)`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 4px;
	margin-left: auto;
	width: 100%;
	@media screen and (max-width: ${BREAK}) {
		flex-direction: column;
		align-items: stretch;
		gap: 2px;
		margin: 0;
		padding: 8px 0 16px;
		width: 100%;
	}
	@media screen and (min-width: ${RAIL_MIN}) {
		/* && beats bootstrap's .navbar-expand-lg .navbar-nav row rule */
		&& {
			flex-direction: column;
			align-items: stretch;
			justify-content: flex-start;
			gap: 2px;
			margin: 0;
		}
	}
`

const TopLink = styled(Nav.Link)`
	display: inline-flex;
	align-items: center;
	gap: 7px;
	text-decoration: none !important;
	font-family: ${({ theme }) => theme.fontFamily};
	font-size: 13.5px;
	font-weight: 500;
	line-height: 20px;
	padding: 6px 11px !important;
	border-radius: 8px;
	& > svg {
		width: 16px;
		height: 16px;
		flex-shrink: 0;
	}
	color: ${({ theme, $active }) => ($active ? theme.text : theme.textSecondary)} !important;
	background-color: ${({ theme, $active }) => ($active ? 'rgba(127, 127, 127, 0.12)' : 'transparent')};
	transition: color ${({ theme }) => theme.motion.fast}, background-color ${({ theme }) => theme.motion.fast};
	&:hover {
		color: ${({ theme }) => theme.text} !important;
	}
	/* no outlines on the nav cells - state is expressed via background only */
	outline: none !important;
	box-shadow: none !important;
	&:focus {
		outline: none;
	}
	&:focus-visible {
		outline: none;
		color: ${({ theme }) => theme.text} !important;
		background-color: rgba(127, 127, 127, 0.12);
	}
	@media screen and (max-width: ${BREAK}) {
		padding: 10px 12px !important;
	}
	@media screen and (min-width: ${RAIL_MIN}) {
		/* stacked: icon over a small centered label (the rail is 125px wide) */
		&& {
			flex-direction: column;
			align-items: center;
			justify-content: center;
			gap: 6px;
			padding: 12px 4px !important;
			font-size: 11.5px;
			line-height: 14px;
			text-align: center;
			white-space: normal;
		}
		& > svg {
			width: 18px;
			height: 18px;
		}
	}
`

const StyledCollapse = styled(Navbar.Collapse)`
	@media screen and (max-width: ${BREAK}) {
		background-color: ${({ theme }) => theme.base};
	}
	@media screen and (min-width: ${RAIL_MIN}) {
		/* bootstrap's .navbar-expand-lg .navbar-collapse centers the link group
		   mid-rail; pin it to the top under the logo instead */
		&& {
			align-items: flex-start;
			flex-grow: 0;
		}
	}
`

const LogoLink = styled(Nav.Link)`
	padding: 0 !important;
	margin-right: 48px;
	text-decoration: none !important;
	/* wordmark svg is hardcoded white; invert it for light mode */
	filter: ${({ theme }) => (theme.name === 'light' ? 'invert(0.88)' : 'none')};
	display: flex;
	align-items: center;
	outline: none;
	box-shadow: none !important;
	&:focus {
		outline: none;
	}
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.borderFocus};
		outline-offset: 4px;
		border-radius: 6px;
	}
	@media screen and (min-width: ${RAIL_MIN}) {
		margin: 0 auto 20px;
		& svg {
			max-width: 100%;
			height: auto;
		}
	}
`

const HorizOnly = styled.span`
	display: flex;
	@media screen and (min-width: ${RAIL_MIN}) {
		display: none;
	}
`

const VertOnly = styled.span`
	display: none;
	@media screen and (min-width: ${RAIL_MIN}) {
		display: flex;
	}
`

const StyledToggle = styled(Navbar.Toggle)`
	margin-left: auto;
	border: none;
	filter: ${({ theme }) => (theme.name === 'light' ? 'invert(0.88)' : 'none')};
	outline: none !important;
	box-shadow: none !important;
	border-radius: 8px;
	&:focus-visible {
		background-color: rgba(127, 127, 127, 0.12);
	}
`

// Icons ride along at 16px and inherit the link's state color via currentColor
// (their built-in active-blue is bypassed by passing no activePaths).
const NavItem = ({ href, paths, icon: Icon, children }) => {
	const isActive = useIsActive(paths || [])
	return (
		<TopLink href={href} $active={isActive}>
			{Icon && <Icon fill='currentColor' />}
			{children}
		</TopLink>
	)
}

export default function Header() {
	return (
		<StyledNav id='navbar' collapseOnSelect expand='lg'>
			<NavInner>
				<LogoLink href='/' aria-label='TezLab home'>
					<HorizOnly><LogoHoriz /></HorizOnly>
					<VertOnly><LogoVert /></VertOnly>
				</LogoLink>
				<StyledToggle aria-controls='responsive-navbar-nav' aria-label='Toggle navigation'>
					<Hamburger />
				</StyledToggle>
				<StyledCollapse id='responsive-navbar-nav'>
					<LinkRow>
						<NavItem href='/subscribe' paths={activePaths.subscribe} icon={Subscription}>Membership</NavItem>
						<NavItem href='/founders' paths={activePaths.founders} icon={FoundersLogo}>Founders Series</NavItem>
						<NavItem href='/software-tracker/tesla' paths={activePaths.software} icon={SoftwareTracker}>Software Tracker</NavItem>
						<NavItem href='/mcp' paths={activePaths.mcp} icon={McpIcon}>MCP</NavItem>
						<NavItem href='/map' paths={activePaths.map} icon={MapIcon}>Charger Map</NavItem>
						{isTesla() && (
							<NavItem href='/dash' paths={activePaths.dash} icon={LiveDrive}>Dashboard</NavItem>
						)}
					</LinkRow>
				</StyledCollapse>
			</NavInner>
		</StyledNav>
	)
}
